import { usePermission } from "@how/app-kit";
import { Anchor, Stack, Text } from "@mantine/core";
import { Link } from "react-router";
import { friendlyZoneName, timeInZone, useMinuteClock } from "@how/ui";

export interface ChurchTimeZoneProps {
  /** The church's zone (an IANA name), from the tenant row every member can read. */
  readonly zone: string;
}

/**
 * The church's time zone, read-only, for everyone who signs in to the staff dashboard (owner decision of
 * 2026-10-05). It lives on Home, which every staff member reaches, because Settings is the owner's alone. The owner
 * gets a link to the field on Settings, which is where the zone changes (settings.time_zone, ADR 0057); everyone else
 * is told who can change it. The server decides again on save; this only words what the viewer may do.
 */
export function ChurchTimeZone({ zone }: ChurchTimeZoneProps) {
  const now = useMinuteClock();
  const canChange = usePermission("settings.time_zone", { write: true });
  // Settings is gated by settings.manage; a link the viewer cannot follow is not offered.
  const canOpenSettings = usePermission("settings.manage");
  const time = timeInZone(zone, now);
  const ownerLink = canChange.data === true && canOpenSettings.data === true;
  const pending = canChange.isPending || canOpenSettings.isPending;
  return (
    <Stack gap={2}>
      <Text size="sm" fw={600}>
        Church time zone
      </Text>
      <Text size="sm">
        {time === null ? friendlyZoneName(zone) : `${friendlyZoneName(zone)} — ${time} there now`}
      </Text>
      {pending ? null : ownerLink ? (
        <Anchor component={Link} to="/settings" size="sm">
          Change it in Settings
        </Anchor>
      ) : (
        <Text size="sm" c="dimmed">
          Only the church owner can change this.
        </Text>
      )}
    </Stack>
  );
}
